import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { buttonStyles, Spinner } from '@zt/ui';
import { api, ApiError } from '../../lib/api';
import { HelpTip } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { useMoney } from '../../lib/money';
import { usePermissions } from '../../lib/session';
import type { SalesReport } from '../../lib/types-faz8';
import { ColumnChart, StatTile } from './charts';
import { presetRange } from './period';

/**
 * Ana paneldeki satış özeti (F8-02): bugün ve son 30 gün (önceki 30 günle), günlük net satış grafiği ve satış
 * raporuna bağlantı. Veri satış raporuyla aynı uçtan gelir (aynı tanımlar, ADR-802). Yetkisi olmayana rakam
 * gösterilmez; tahmini veya örnek rakam yoktur.
 */
export function DashboardSales() {
  const t = useT();
  const { can } = usePermissions();
  const allowed = can('reports.sales.read');
  const range = presetRange('last_30');
  const q = useQuery({
    queryKey: ['dashboard-sales', range.from, range.to],
    queryFn: () =>
      api.get<SalesReport>(`/v1/admin/reports/sales?from=${range.from}&to=${range.to}&interval=day`),
    enabled: allowed,
  });
  if (!allowed) return <p className="max-w-[68ch] text-ink-2">{t('admin.reports.dashboard.noPermission')}</p>;
  if (q.isError)
    return (
      <p role="alert" className="text-sm text-rose">
        {q.error instanceof ApiError ? q.error.title : String(q.error)}
      </p>
    );
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <Body d={q.data} />;
}

function Body({ d }: { d: SalesReport }) {
  const t = useT();
  const money = useMoney();
  const { locale } = useI18n();
  const tag = locale === 'en' ? 'en-GB' : locale === 'de' ? 'de-DE' : locale === 'ar' ? 'ar' : 'tr-TR';
  const tl = (n: number | null) => money(n, 'TRY');
  const today = d.series.at(-1);
  const yesterday = d.series.at(-2);
  const label = (b: string) =>
    new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
      new Date(`${b}T12:00:00Z`),
    );
  const compact = (minor: number) =>
    new Intl.NumberFormat(tag, {
      style: 'currency',
      currency: 'TRY',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(minor / 100);
  return (
    <div className="space-y-4">
      <section
        aria-label={t('admin.reports.dashboard.tilesLabel')}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatTile
          label={t('admin.reports.dashboard.today')}
          value={tl(today?.netSales ?? 0)}
          current={today?.netSales ?? 0}
          previous={yesterday?.netSales ?? 0}
          previousLabel={tl(yesterday?.netSales ?? 0)}
          comparedTo={t('admin.reports.dashboard.yesterday')}
        />
        <StatTile
          label={t('admin.reports.dashboard.todayOrders')}
          value={String(today?.orders ?? 0)}
          current={today?.orders ?? 0}
          previous={yesterday?.orders ?? 0}
          previousLabel={String(yesterday?.orders ?? 0)}
          comparedTo={t('admin.reports.dashboard.yesterday')}
        />
        <StatTile
          label={t('admin.reports.dashboard.last30')}
          value={tl(d.totals.netSales)}
          current={d.totals.netSales}
          previous={d.previous.netSales}
          previousLabel={tl(d.previous.netSales)}
        />
        <StatTile
          label={t('admin.reports.dashboard.last30Orders')}
          value={String(d.totals.activeOrders)}
          current={d.totals.activeOrders}
          previous={d.previous.activeOrders}
          previousLabel={String(d.previous.activeOrders)}
        />
      </section>
      {d.awaitingPayment.orders > 0 && (
        <p className="text-sm text-ink-3">
          {t('admin.salesReport.awaiting', {
            count: d.awaitingPayment.orders,
            amount: tl(d.awaitingPayment.amountMinor),
          })}
        </p>
      )}
      <ColumnChart
        title={t('admin.reports.dashboard.chartTitle')}
        points={d.series.map((s) => ({ key: s.bucket, label: label(s.bucket), value: s.netSales }))}
        formatValue={tl}
        formatTick={compact}
      />
      <div className="flex items-center gap-2">
        <Link to="/satis-raporu" className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.reports.dashboard.open')}
        </Link>
        <HelpTip k="dashboard.sales" />
      </div>
    </div>
  );
}
